/* eslint-disable no-underscore-dangle */
import OktaAuth, { AuthState as OktaAuthState } from '@okta/okta-auth-js'
import {
  API,
  ACCOUNT_LOCKOUT_MAX_ATTEMPTS,
  ACCOUNT_LOCKOUT_DURATION_MINUTES,
  AUTH_COOKIE_EXPIRATION_HOURS,
  LOGIN_ERROR_MESSAGE,
  OKTA_CACHE_STORAGE,
  OKTA_SHARED_TRANSACTION_STORAGE,
  OKTA_ORIGINAL_URI_STORAGE,
  OKTA_TOKEN_STORAGE,
  OKTA_TOKEN_STORAGE_KEYS,
  AUTH_ERROR_TYPES,
} from '@constants'
import { configs } from '@configs'
import { Brand } from '@typescript'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { RegistrationContext } from '@account/hooks/useRegistration/_reducer'
import { parseApiErrorResponse } from '@utils/errorParser'
import { createLogger } from '@utils/Logger'
import { serverUrl } from '@utils/MkEnv'
import { formatAccountErrorMessage as formatAccountError } from '@utils/errorMessageFormatter'
import { OktaAuthnResponse, OktaUser, OktaPasswordPolicy } from '@hooks/useOkta'
import {
  TMkAuthenticationAction,
  EMkAuthenticationAction,
  TMkAuthenticationState,
  IMkUser,
  IMkAuthorizedSession,
  IMkAuthenticatedSession,
  TMkSession,
} from '@features/authentication/types'
import { AuthenticationError, AuthorizationError } from '@features/authentication/errors'
import {
  LOGIN_PATHS,
  isProtectedRoute,
  ACCOUNT_CONTEXT_CACHE_KEYS,
  getApexDomain,
  OKTA_ACCESS_DENIED_ERROR,
  OKTA_ACCESS_DENIED_ERROR_TEXT_EBAC,
  OKTA_ACCESS_DENIED_ERROR_TEXT,
  OKTA_ACCESS_DENIED_ALT_ERROR_TEXT,
  OKTA_INVALID_SCOPE_ERROR_TEXT,
  OKTA_SERVER_ERROR_TEXT,
} from '@auth/constants/authConstants'
import { saveRegistrationContext } from '@utils/registrationContext'
import { isPlausibleMfaEnrollmentEmail, resolveAuthnMfaDisplayEmail } from '@features/authentication/utils/resolveAuthnMfaDisplayEmail'
import { getLockoutVariant, isUsernameLocked, isPasswordLockoutActive } from '@features/authentication/utils'
import { IAuthenticationService, IErrorCallbackArgs } from './IAuthService'
import { mockAuthorizeOnCallback, mockLogin } from './mockAuth'

const getOktaErrorMessage = ({ error, errorDescription, brand }: IErrorCallbackArgs): string => {
  const accessDeniedError = brand === 'ebac' ? OKTA_ACCESS_DENIED_ERROR_TEXT_EBAC : OKTA_ACCESS_DENIED_ERROR_TEXT

  const errorMap: Record<string, string> = {
    access_denied: errorDescription?.includes(OKTA_ACCESS_DENIED_ERROR) ? accessDeniedError : OKTA_ACCESS_DENIED_ALT_ERROR_TEXT,
    invalid_scope: OKTA_INVALID_SCOPE_ERROR_TEXT,
    server_error: OKTA_SERVER_ERROR_TEXT,
  }

  return errorMap[error] || 'Authentication failed'
}

const handleOktaError = ({ error, errorDescription, brand }: IErrorCallbackArgs): AuthorizationError => {
  const errorMessage = getOktaErrorMessage({ error, errorDescription, brand })
  return new AuthorizationError(errorMessage)
}

const getAuthnProfileName = (authnResponse: OktaAuthnResponse, field: 'firstName' | 'lastName'): string =>
  authnResponse._embedded?.user?.profile?.[field] || authnResponse.data?._embedded?.user?.profile?.[field] || authnResponse.user?.profile?.[field] || ''

export class MkAuthenticationService extends IAuthenticationService {
  private static readonly SELECTED_MEMBER_NUMBER_STORAGE_KEY = 'mkMemberNumber'

  private makoState: TMkAuthenticationState

  private dispatch: React.Dispatch<TMkAuthenticationAction>

  private oktaAuth!: OktaAuth

  private oktaState!: OktaAuthState | null

  private oktaConfig: typeof configs.oidc

  private authBrand: string

  private logger = createLogger('AuthenticationService')

  private static readonly MFA_TRIGGER_TTL_MS = 15000

  private static recentlyTriggeredMfa: Map<string, number> = new Map()

  private static cleanupRecentlyTriggeredMfa(): void {
    const now = Date.now()
    this.recentlyTriggeredMfa.forEach((timestamp, stateToken) => {
      if (now - timestamp >= this.MFA_TRIGGER_TTL_MS) {
        this.recentlyTriggeredMfa.delete(stateToken)
      }
    })
  }

  private static recentlyTriggeredMfaCleanupInterval: NodeJS.Timeout | null = setInterval(() => {
    MkAuthenticationService.cleanupRecentlyTriggeredMfa()
  }, 60000)

  public static cleanupRecentlyTriggeredMfaInterval(): void {
    if (MkAuthenticationService.recentlyTriggeredMfaCleanupInterval !== null) {
      clearInterval(MkAuthenticationService.recentlyTriggeredMfaCleanupInterval)
      MkAuthenticationService.recentlyTriggeredMfaCleanupInterval = null
    }
  }

  public static cleanupMfaInterval(): void {
    if (this.recentlyTriggeredMfaCleanupInterval) {
      clearInterval(this.recentlyTriggeredMfaCleanupInterval)
      this.recentlyTriggeredMfaCleanupInterval = null
    }
  }

  private static hasRecentlyTriggeredMfa(stateToken?: string): boolean {
    if (!stateToken) return false
    const last = this.recentlyTriggeredMfa.get(stateToken)
    if (!last) return false
    const withinTtl = Date.now() - last < this.MFA_TRIGGER_TTL_MS
    if (!withinTtl) {
      this.recentlyTriggeredMfa.delete(stateToken)
    }
    return withinTtl
  }

  private static markMfaTriggered(stateToken?: string): void {
    if (!stateToken) return
    this.recentlyTriggeredMfa.set(stateToken, Date.now())
  }

  private static MK_AUTH_TOKEN_TTL_MIN: number = Number(process.env.MK_AUTH_TOKEN_TTL_MIN) || 45

  // --- Session TTL helpers (45 minutes) ---
  private static readonly MK_AUTH_TOKEN_TTL_MS = MkAuthenticationService.MK_AUTH_TOKEN_TTL_MIN * 60 * 1000

  private static setMakoAuthTokensExpiry(): void {
    try {
      ProtectedDataStore.setItem<number>(EMkStorageKeys.MAKO_AUTH_TOKENS_EXPIRES_AT, Date.now() + MkAuthenticationService.MK_AUTH_TOKEN_TTL_MS)
    } catch {
      // ignore storage write errors
    }
  }

  private isRefreshingMakoAuthTokens: boolean = false

  /** Sync read of client-side Mako auth token TTL (see MK_AUTH_TOKEN_TTL_MIN). Used by guards and wishlist fetch gating. */
  public static areMakoAuthTokensExpired(): boolean {
    try {
      const exp = ProtectedDataStore.getItem<number>(EMkStorageKeys.MAKO_AUTH_TOKENS_EXPIRES_AT)
      return typeof exp === 'number' ? Date.now() > exp : false
    } catch {
      return false
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private isMakoAuthTokensExpired(): boolean {
    return MkAuthenticationService.areMakoAuthTokensExpired()
  }
  // --- End TTL helpers ---

  constructor(
    state: TMkAuthenticationState,
    dispatch: React.Dispatch<TMkAuthenticationAction>,
    { oktaAuth, oktaAuthState }: { oktaAuth?: OktaAuth | null; oktaAuthState?: OktaAuthState | null } = {},
  ) {
    super()
    this.makoState = {
      ...state,
      ...(oktaAuthState && state.isAuthorized ? (oktaAuthState as OktaAuthState as any) : {}),
    }
    this.dispatch = dispatch
    this.authBrand = (state as any).authBrand as string

    this.oktaConfig = configs.oidc
    if (oktaAuth) {
      this.oktaAuth = oktaAuth
    }
    if (oktaAuthState) {
      this.oktaState = oktaAuthState
    }
  }

  public getContextProps<T>(): T {
    const props = {
      authService: this,
      ...this.makoState,
      dispatchAuthenticationAction: this.dispatch,
      login: this.login.bind(this),
      loginWithToken: this.loginWithToken.bind(this),
      logout: this.logout.bind(this),
      onLoginCallback: this.onLoginCallback.bind(this),
      refreshSession: this.refreshSession?.bind(this),
      switchMembership: this.switchMembership.bind(this),
      renewSession: this.renewSession.bind(this),
      refreshMakoAuthorizationTokens: this.refreshMakoAuthorizationTokens?.bind(this),
    } as unknown as T
    return props
  }

  public refreshSession?: (() => Promise<void>) | undefined

  public async login({ username, password }: { username: string; password: string }): Promise<void> {
    // Step 1: Clear storage FIRST
    await this.clearStaleSessions()

    // Step 2: THEN reset Redux state (reads empty storage now)
    this.dispatch({ type: EMkAuthenticationAction.RESET_STATE })

    // Step 3: Continue with fresh login
    this.dispatch({ type: EMkAuthenticationAction.AUTHENTICATION_REQUEST })

    // Check if account is currently locked for this username
    const lockoutVariant = getLockoutVariant(this.makoState)
    if (lockoutVariant !== 'NONE' && isUsernameLocked(this.makoState, username)) {
      if (lockoutVariant === 'MFA_CRITICAL') {
        // MFA critical lockout - cannot be automatically unlocked, requires phone support
        throw new AuthenticationError(AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED)
      }

      if (lockoutVariant === 'PASSWORD_TEMPORARY') {
        // Password lockout - check if still within time limit
        if (isPasswordLockoutActive(this.makoState)) {
          // Still locked - extend the lockout timer and block login
          const newLockoutEndTime = Date.now() + ACCOUNT_LOCKOUT_DURATION_MINUTES * 60 * 1000
          this.dispatch({
            type: EMkAuthenticationAction.LOCK_ACCOUNT,
            payload: { lockoutEndTime: newLockoutEndTime, username },
          })
          throw new AuthenticationError(AUTH_ERROR_TYPES.ACCOUNT_TEMPORARILY_LOCKED)
        } else {
          // Lockout period has expired, unlock the account
          this.dispatch({ type: EMkAuthenticationAction.UNLOCK_ACCOUNT })
        }
      }
    }

    try {
      if (process.env.NODE_ENV === 'development' && window.location.pathname.includes('/test/')) {
        await mockLogin(
          username,
          this.initializeSession.bind(this),
          this.authorizeSession.bind(this),
          () => ProtectedDataStore.getItem<IMkAuthenticatedSession>(EMkStorageKeys.MAKO_SESSION),
          this.dispatch,
        )
        return
      }

      const { user: sessionUser, sessionToken, codeVerifier } = await this.authenticateWithOkta(username, password)
      await this.initializeSession(sessionUser, sessionToken, codeVerifier, username)
      this.dispatch({ type: EMkAuthenticationAction.RESET_FAILED_ATTEMPTS })
    } catch (err) {
      // Handle MFA-related errors by dispatching to state, emitting event, and re-throwing for UI handling
      if (err instanceof AuthenticationError && (err.message === AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED || err.message === AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED)) {
        this.dispatch({
          type: EMkAuthenticationAction.AUTHENTICATION_FAILURE,
          payload: err,
        })
        // Emit the authorization error event so AuthErrorHandler can catch it
        this.emitAuthorizationError(err)
        throw err
      } else {
        // For other errors, handleLoginError will dispatch and throw, so we just re-throw its result
        this.handleLoginError(err, username)
      }
    }
  }

  private handleLoginError(err: unknown, username: string): never {
    // Don't increment failed attempts for Okta-managed lockouts or network errors
    const isOktaLockout = err instanceof AuthenticationError && err.message === 'OKTA_ACCOUNT_LOCKED'
    const isNetworkError = this.isNetworkError(err)

    if (!isOktaLockout && !isNetworkError) {
      // Increment failed attempts
      this.dispatch({ type: EMkAuthenticationAction.INCREMENT_FAILED_ATTEMPTS })

      // Check if we should lock the account locally
      const newFailedAttempts = this.makoState.failedLoginAttempts + 1
      if (newFailedAttempts >= ACCOUNT_LOCKOUT_MAX_ATTEMPTS) {
        const lockoutEndTime = Date.now() + ACCOUNT_LOCKOUT_DURATION_MINUTES * 60 * 1000
        this.dispatch({
          type: EMkAuthenticationAction.LOCK_ACCOUNT,
          payload: { lockoutEndTime, username },
        })
      }
    }

    // Preserve specific error messages that should be shown to the user
    const preservedErrorMessages: string[] = [
      AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED,
      AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED,
      AUTH_ERROR_TYPES.ACCOUNT_TEMPORARILY_LOCKED,
      AUTH_ERROR_TYPES.PASSWORD_EXPIRED,
      AUTH_ERROR_TYPES.BRAND_MISMATCH,
    ]

    const shouldPreserveMessage = err instanceof AuthenticationError && (preservedErrorMessages as readonly string[]).includes(err.message)
    let errorMessage: string

    if (shouldPreserveMessage) {
      errorMessage = (err as AuthenticationError).message
    } else if (isNetworkError) {
      errorMessage = MkAuthenticationService.getNetworkErrorMessage(err)
    } else {
      errorMessage = LOGIN_ERROR_MESSAGE
    }

    const authError = new AuthenticationError(errorMessage, err)
    this.dispatch({ type: EMkAuthenticationAction.AUTHENTICATION_FAILURE, payload: authError })

    // Emit global authorization error event for account lockout to ensure modal shows
    if (err instanceof AuthenticationError && err.message === AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED) {
      this.emitAuthorizationError(authError)
    }

    throw authError
  }

  // eslint-disable-next-line class-methods-use-this
  private isNetworkError(err: unknown): boolean {
    if (err instanceof Error) {
      const message = err.message.toLowerCase()

      // Check for network-related keywords
      const isNetworkKeyword =
        message.includes('network') ||
        message.includes('fetch') ||
        message.includes('connection') ||
        message.includes('timeout') ||
        message.includes('unreachable') ||
        message.includes('failed to fetch') ||
        message.includes('network error') ||
        message.includes('connection refused') ||
        message.includes('connection timeout')

      // Check for HTTP status codes that indicate network/server issues
      const isHttpError =
        message.includes('403') ||
        message.includes('500') ||
        message.includes('502') ||
        message.includes('503') ||
        message.includes('504') ||
        message.includes('forbidden') ||
        message.includes('server error') ||
        message.includes('service unavailable')

      const isNetwork = isNetworkKeyword || isHttpError
      return isNetwork
    }
    return false
  }

  private static getNetworkErrorMessage(err: unknown): string {
    if (err instanceof Error) {
      const message = err.message.toLowerCase()

      // Handle specific HTTP status codes
      if (message.includes('401') || message.includes('unauthorized')) {
        return 'Authentication service is temporarily unavailable. Please try again in a few moments.'
      }
      if (message.includes('403') || message.includes('forbidden')) {
        return 'Access to the authentication service is temporarily restricted. Please try again later.'
      }
      if (message.includes('500') || message.includes('server error')) {
        return 'The authentication server is experiencing issues. Please try again in a few moments.'
      }
      if (message.includes('502') || message.includes('503') || message.includes('504') || message.includes('service unavailable')) {
        return 'The authentication service is temporarily unavailable. Please try again in a few moments.'
      }

      // Handle network-related issues
      if (message.includes('timeout')) {
        return 'The request timed out. Please check your internet connection and try again.'
      }
      if (message.includes('unreachable') || message.includes('connection refused')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.'
      }
      if (message.includes('failed to fetch')) {
        return 'Network error occurred. Please check your internet connection and try again.'
      }
    }
    return 'A network error occurred. Please check your internet connection and try again.'
  }

  // eslint-disable-next-line class-methods-use-this
  private emitAuthorizationError(authError: AuthenticationError | AuthorizationError): void {
    try {
      window.dispatchEvent(
        new CustomEvent('authorizationError', {
          detail: { errorMessage: authError.message, errorType: authError.name },
        }),
      )
    } catch (e) {
      // Failed to emit authorizationError event
    }
  }

  public async loginWithToken(token: string, isActivation: boolean): Promise<OktaAuthnResponse> {
    try {
      const result = await this.authenticateWithToken(token, isActivation)

      // For activation tokens, the expected status is 'PASSWORD_RESET', not 'SUCCESS'
      if (result.status !== 'SUCCESS' && result.status !== 'PASSWORD_RESET') {
        throw new AuthorizationError('Activation token processing failed')
      }

      // For PASSWORD_RESET status, user data can be in the _embedded field
      // Extract user data from _embedded.user if available
      let user = result?.user as OktaUser

      // Check for user data in _embedded field for PASSWORD_RESET status
      const resultWithEmbedded = result as any
      const embeddedField = resultWithEmbedded['_embedded'] // eslint-disable-line @typescript-eslint/dot-notation
      if (embeddedField?.user && (!user || Object.keys(user || {}).length === 0)) {
        // Extract user data from _embedded.user
        const embeddedUser = embeddedField.user
        user = {
          id: embeddedUser.id || '',
          login: embeddedUser.profile?.login || embeddedUser.login || '',
          email: embeddedUser.profile?.email || embeddedUser.email || embeddedUser.profile?.login || embeddedUser.login || '',
          profile: {
            firstName: embeddedUser.profile?.firstName || embeddedUser.firstName || '',
            lastName: embeddedUser.profile?.lastName || embeddedUser.lastName || '',
            displayName:
              embeddedUser.profile?.displayName ||
              embeddedUser.displayName ||
              `${embeddedUser.profile?.firstName || embeddedUser.firstName || ''} ${embeddedUser.profile?.lastName || embeddedUser.lastName || ''}`.trim() ||
              embeddedUser.profile?.login ||
              embeddedUser.login ||
              'User',
            email: embeddedUser.profile?.email || embeddedUser.email || undefined,
          },
        }
      }

      // If still no user data, create a minimal user object
      if (!user) {
        user = {
          id: '', // Will be populated later
          login: '', // Will be populated later
          email: '', // Will be populated later
          profile: {
            firstName: '',
            lastName: '',
            displayName: '',
          },
        }
      }

      // At this point, user should be available (either from result.user or extracted from _embedded)
      if (!user) {
        throw new AuthorizationError('Invalid activation token response - missing user information')
      }

      // Extract password policy and reset URL from _embedded and _links
      let passwordPolicy: OktaPasswordPolicy | undefined
      let resetPasswordUrl: string | undefined

      if (embeddedField?.policy?.complexity) {
        passwordPolicy = {
          minLength: embeddedField.policy.complexity.minLength || 8,
          minLowerCase: embeddedField.policy.complexity.minLowerCase || 1,
          minUpperCase: embeddedField.policy.complexity.minUpperCase || 1,
          minNumber: embeddedField.policy.complexity.minNumber || 1,
          minSymbol: embeddedField.policy.complexity.minSymbol || 0,
          excludeUsername: embeddedField.policy.complexity.excludeUsername || false,
        }
      }

      if (resultWithEmbedded._links?.next?.href) {
        resetPasswordUrl = resultWithEmbedded._links.next.href
      }

      // Preserve the original Okta payload fields and attach normalized user
      const authnResponse: OktaAuthnResponse = {
        ...(result as any),
        user,
        stateToken: result.stateToken || '',
        sessionToken: (result as any).sessionToken || '',
        status: result.status,
        passwordPolicy,
        resetPasswordUrl,
      }

      return authnResponse
    } catch (err) {
      // Provide user-friendly error messages for common activation issues
      if (err instanceof Error) {
        if (err.message.includes('invalid_token')) {
          throw new AuthorizationError('Invalid or expired activation token. Please request a new activation link.')
        }
        if (err.message.includes('token_expired')) {
          throw new AuthorizationError('Activation token has expired. Please request a new activation link.')
        }
        if (err.message.includes('user_not_found')) {
          throw new AuthorizationError('User account not found. Please contact support.')
        }
      }

      throw err
    }
  }

  public async onLoginCallback(onAuthorizedSession: (session: IMkAuthorizedSession) => void, onFailedToAuthorize: (error?: Error) => void): Promise<void> {
    try {
      // For development on test pages, use mock authorization
      if (process.env.NODE_ENV === 'development' && window.location.pathname.includes('/test/')) {
        await mockAuthorizeOnCallback(
          () => ProtectedDataStore.getItem<IMkAuthenticatedSession>(EMkStorageKeys.MAKO_SESSION),
          this.authorizeSession.bind(this),
          onAuthorizedSession,
          onFailedToAuthorize,
        )
        return
      }

      // Check for error parameters in URL before processing
      const urlParams = new URLSearchParams(window.location.search)
      const error = urlParams.get('error')
      const errorDescription = urlParams.get('error_description') as string

      if (error) {
        this.handleUrlError(error, errorDescription, onFailedToAuthorize)
        return
      }

      await this.oktaAuth.handleLoginRedirect()

      const tokens = await this.oktaAuth.tokenManager.getTokens()

      if (!tokens || !tokens.accessToken?.accessToken || !tokens.idToken?.claims.preferred_username) {
        this.handleInvalidTokens(onFailedToAuthorize)
        return
      }

      const accessToken = String(tokens.accessToken.accessToken)
      const userName = String(tokens.idToken.claims.preferred_username)

      // Validate JWT brand (non-critical, so we don't fail the entire flow)
      await this.validateJwtBrand(accessToken)

      // Handle session consistency - this will throw if there's a mismatch
      await this.handleSessionConsistency(userName)

      const selectedMemberNumber = this.getSelectedMemberNumberFromStorage()
      const { authenticatedSession, makoUser } = await this.authorizeWithMako({
        accessToken,
        userName,
        memberNumber: selectedMemberNumber,
      })

      // Only save username to localStorage AFTER successful mismatch check
      // This prevents overwriting the correct username with a mismatched one from cached sessions
      const savedRememberMe = DataStore.getItem('rememberMe', { store: localStorage })
      if (savedRememberMe === 'true' && userName) {
        DataStore.setItem('savedUsername', userName, { store: localStorage })
      }

      if (!makoUser) {
        throw new AuthorizationError('Mako user is undefined')
      }

      const authorizedSession = await this.authorizeSession(authenticatedSession, makoUser, userName, tokens)
      onAuthorizedSession(authorizedSession)
    } catch (error) {
      this.handleLoginCallbackError(error, onFailedToAuthorize)
    }
  }

  private handleUrlError(error: string, errorDescription: string, onFailedToAuthorize: (error?: Error) => void): void {
    const authError = handleOktaError({ error, errorDescription, brand: this.authBrand })

    this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_FAILURE, payload: authError })
    this.emitAuthorizationError(authError)

    onFailedToAuthorize(authError)
  }

  private handleInvalidTokens(onFailedToAuthorize: (error?: Error) => void): void {
    const authError = new AuthorizationError('Invalid tokens received during login callback')

    this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_FAILURE, payload: authError })
    this.emitAuthorizationError(authError)

    onFailedToAuthorize(authError)
  }

  private async validateJwtBrand(accessToken: string): Promise<void> {
    try {
      const { validateJwtBrand } = await import('@utils/authJwtBrand')
      const result = validateJwtBrand(this.logger, accessToken, this.authBrand)
      if (!result.ok) {
        // Brand mismatch should be treated as an AuthenticationError, not AuthorizationError
        throw new AuthenticationError(result.error.message)
      }
    } catch (e) {
      // Re-throw AuthenticationError for brand mismatch, but catch other errors
      if (e instanceof AuthenticationError && e.message === AUTH_ERROR_TYPES.BRAND_MISMATCH) {
        throw e
      }
      // For other errors, log and continue (non-critical validation)
      this.logger.warn('Failed to validate JWT brand', e)
    }
  }

  private async handleSessionConsistency(userName: string): Promise<void> {
    const storedSession = this.makoState.session
    const sessionUsername =
      storedSession?.sessionType === 'authorized' ? (storedSession as IMkAuthorizedSession)?.authenticatedUserName : (storedSession as IMkAuthenticatedSession)?.username

    if (storedSession && sessionUsername && sessionUsername.toLowerCase() !== userName.toLowerCase()) {
      await this.clearStaleSessions()
    }
  }

  private handleLoginCallbackError(error: unknown, onFailedToAuthorize: (error?: Error) => void): void {
    // Try to extract error type from the error object, fallback to 'access_denied'
    const errorType = (error as any).errorCode || (error as any).error || (error as any).name || 'access_denied'

    // Check if this is a preserved authentication error (like BRAND_MISMATCH)
    const isPreservedAuthError =
      error instanceof AuthenticationError &&
      (AUTH_ERROR_TYPES.BRAND_MISMATCH === error.message || AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED === error.message || AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED === error.message)

    // Check if this is a Mako authorization error with a detailed message
    let errorMessage: string
    if (isPreservedAuthError) {
      // Preserve the error message for UI parsing
      errorMessage = error.message
    } else if (error instanceof AuthorizationError && error.message && error.message !== 'Authentication failed') {
      // This is a Mako authorization error with a detailed message, preserve it
      errorMessage = error.message
    } else {
      // This might be an Okta error, try to get appropriate message
      errorMessage = getOktaErrorMessage({ error: errorType, errorDescription: (error as any).message, brand: this.authBrand }) || (error as any).message
    }

    // Use the appropriate error type based on the original error
    const authError = isPreservedAuthError ? new AuthenticationError(errorMessage, error) : new AuthorizationError(errorMessage, error)

    this.dispatch({
      type: isPreservedAuthError ? EMkAuthenticationAction.AUTHENTICATION_FAILURE : EMkAuthenticationAction.AUTHORIZATION_FAILURE,
      payload: authError,
    })

    // Emit authorization error event for all errors (including brand mismatch)
    this.emitAuthorizationError(authError)

    onFailedToAuthorize(authError)
  }

  public async logout(): Promise<void> {
    try {
      const returnPath = window.location.pathname

      // Normalize the path - remove leading '/mk' if present to avoid duplication
      const normalizedPath = returnPath.startsWith('/mk') ? returnPath.substring(3) : returnPath

      // Determine the returnTo value based on current location
      // Use isProtectedRoute to check if the current path is protected
      const returnTo = isProtectedRoute(normalizedPath) ? '/login' : normalizedPath || '/'

      // Clear all local state IMMEDIATELY before redirect
      // This ensures menu and UI reflect logged-out state right away

      // CRITICAL FIX: Order of operations matters!
      // Step 1: Clear storage FIRST via destroySession()
      await this.destroySession()

      // Step 2: THEN reset Redux state (reads empty storage now)
      this.dispatch({ type: EMkAuthenticationAction.RESET_STATE })

      // Step 3: Mark logout complete
      this.dispatch({ type: EMkAuthenticationAction.LOGOUT })

      // Persist redirect intent and destination for callback
      sessionStorage.setItem('logoutReturnTo', returnTo)
      sessionStorage.setItem('mk_logout_redirect', '1')

      // Prefer org-level Okta sign-out to ensure SSO cookie is cleared
      try {
        const orgDomain = MkAuthenticationService.extractOktaOrgDomain(this.oktaConfig.issuer)
        const postLogoutRedirectUri = `${window.location.origin}/mk/account/logout/callback`
        sessionStorage.setItem('mk_org_logout', '1')
        window.location.assign(`${orgDomain}/login/signout?fromURI=${encodeURIComponent(postLogoutRedirectUri)}`)
        return await new Promise<never>(() => {})
      } catch (e) {
        // Fallback: go directly to our logout callback to finish local cleanup
        window.location.assign(`${window.location.origin}/mk/account/logout/callback`)
        return await new Promise<never>(() => {})
      }
    } catch (error) {
      // Final safety: force navigation to callback
      sessionStorage.removeItem('mk_logout_redirect')
      window.location.assign(`${window.location.origin}/mk/account/logout/callback`)
      return new Promise<never>(() => {})
    }
  }

  public async renewSession(): Promise<void> {
    try {
      if (!this.oktaAuth) {
        throw new Error('Okta auth is not initialized')
      }

      // Refresh the tokens
      await this.oktaAuth.tokenManager.renew('accessToken')
      await this.oktaAuth.tokenManager.renew('idToken')

      // Dispatch session renewed action
      this.dispatch({ type: EMkAuthenticationAction.SESSION_RENEWED })
    } catch (error) {
      throw new Error(`Session renewal failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Enroll an MFA factor for a user
   * @param stateToken - The Okta state token from authentication
   * @param factorType - The type of MFA factor (email, sms, call)
   * @param email - Email address (required for email factor)
   * @param phoneNumber - Phone number (required for sms/call factors)
   * @returns Enrollment response with factorId, stateToken, verifyLink and resendLink
   */
  // eslint-disable-next-line class-methods-use-this
  public async enrollMfaFactor({ stateToken, factorType, email, phoneNumber }: { stateToken: string; factorType: string; email?: string; phoneNumber?: string }): Promise<{
    factorId: string
    stateToken: string
    verifyLink?: string
    resendLink?: string
    isActivationFlow?: boolean
  }> {
    const payload: any = {
      factorType,
      provider: 'OKTA',
      stateToken,
    }

    // Add profile based on factor type
    if (factorType === 'email') {
      if (!email || !isPlausibleMfaEnrollmentEmail(String(email))) {
        throw new Error('Email address is required for email MFA factor')
      }
      payload.profile = {
        email: String(email).trim(),
      }
    } else if (factorType === 'sms' || factorType === 'call') {
      if (!phoneNumber) {
        throw new Error('Phone number is required for SMS/Call MFA factor')
      }
      payload.profile = {
        phoneNumber,
      }
    }

    const response = await fetch(`${serverUrl}${API}/okta/authn/factors`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(`MFA enrollment failed: ${errorData.errorSummary || response.statusText}`)
    }

    const data = await response.json()

    // Extract factor ID from the response - check multiple possible locations
    // Okta API can return factor ID in different locations depending on response structure
    let factorId = data.factor?.id || data.id

    // If not found at top level, check _embedded structure
    if (!factorId && data._embedded) {
      // Check if _embedded.factor is an array
      if (Array.isArray(data._embedded.factor)) {
        // Take the first factor (most common case for enrollment)
        factorId = data._embedded.factor[0]?.id
      } else if (data._embedded.factor) {
        // Handle single factor object
        factorId = data._embedded.factor.id
      }
      // Also check _embedded.factors (plural)
      if (!factorId && Array.isArray(data._embedded.factors)) {
        factorId = data._embedded.factors[0]?.id
      }
    }

    if (!factorId) {
      throw new Error('MFA enrollment failed: Unable to extract factor ID from response')
    }

    const factorIdString = String(factorId)
    const verifyLink = data._links?.next?.href
    const resendLink = Array.isArray(data._links?.resend) ? data._links.resend[0]?.href : data._links?.resend?.href
    const isActivationFlow = data.status === 'MFA_ENROLL_ACTIVATE'

    return {
      factorId: factorIdString,
      stateToken: data.stateToken || stateToken,
      verifyLink,
      resendLink,
      isActivationFlow,
    }
  }

  /**
   * Update the service state without recreating the instance
   * This method allows the provider to update state efficiently
   */
  public updateState(newState: Partial<TMkAuthenticationState> & { authBrand?: string }): void {
    this.makoState = { ...this.makoState, ...newState }
    if (typeof newState.authBrand === 'string') {
      this.authBrand = newState.authBrand
    }
  }

  /**
   * Build/refresh local authorized session from existing Okta tokens.
   * Used by new tabs (or token-sync updates) so users remain signed in without re-auth.
   */
  public async syncAuthorizedSessionFromOktaTokens(): Promise<boolean> {
    if (!this.oktaAuth) {
      return false
    }

    try {
      const tokens = await this.oktaAuth.tokenManager.getTokens()
      const accessToken = tokens?.accessToken?.accessToken
      const userName = tokens?.idToken?.claims?.preferred_username

      if (!accessToken || !userName) {
        return false
      }

      const currentSession = this.makoState.session
      if (currentSession?.sessionType === 'authorized') {
        const currentUserName = (currentSession as IMkAuthorizedSession).authenticatedUserName
        if (currentUserName && currentUserName.toLowerCase() === String(userName).toLowerCase()) {
          return true
        }
      }

      const selectedMemberNumber = this.getSelectedMemberNumberFromStorage()
      const { authenticatedSession, makoUser } = await this.authorizeWithMako({
        accessToken: String(accessToken),
        userName: String(userName),
        memberNumber: selectedMemberNumber,
      })

      await this.authorizeSession(authenticatedSession, makoUser, String(userName), tokens as any)
      return true
    } catch (error) {
      this.logger.warn('Failed to sync authorized session from Okta tokens', error)
      return false
    }
  }

  /**
   * Clear local session state when another tab signs out and Okta tokens disappear.
   */
  public async clearLocalSessionAfterExternalLogout(): Promise<void> {
    if (!this.oktaAuth) {
      return
    }

    try {
      const tokens = await this.oktaAuth.tokenManager.getTokens()
      const hasAccessToken = !!tokens?.accessToken?.accessToken
      const hasIdToken = !!tokens?.idToken?.idToken

      if (hasAccessToken && hasIdToken) {
        return
      }

      await this.destroySession()
      this.dispatch({ type: EMkAuthenticationAction.RESET_STATE })
      this.dispatch({ type: EMkAuthenticationAction.LOGOUT })
    } catch (error) {
      this.logger.warn('Failed to clear local session after external logout', error)
    }
  }

  private setAuthenticationOrigin(origin: string): void {
    // Check if there's already a stored redirect that's not the login page
    // This preserves redirectTo parameters passed to the login page
    const existingOrigin = DataStore.getItem<string>(EMkStorageKeys.LOGIN_ORIGIN)

    // Only set the origin if:
    // 1. There's no existing origin, or the existing origin is just a login page path
    // 2. AND the new origin is not a login page path (it's a valid redirect target)
    const canSetOrigin = !existingOrigin || LOGIN_PATHS.includes(existingOrigin as (typeof LOGIN_PATHS)[number])
    const isNewOriginValid = !LOGIN_PATHS.includes(origin as (typeof LOGIN_PATHS)[number])

    if (canSetOrigin && isNewOriginValid) {
      DataStore.setItem(EMkStorageKeys.LOGIN_ORIGIN, origin)
      this.dispatch({
        type: EMkAuthenticationAction.SET_AUTHENTICATION_ORIGIN,
        payload: origin,
      })
    }
  }

  private removeAuthenticationOrigin(): void {
    DataStore.removeItem(EMkStorageKeys.LOGIN_ORIGIN)
    this.dispatch({
      type: EMkAuthenticationAction.REMOVE_AUTHENTICATION_ORIGIN,
    })
  }

  private async initializeSession(user: Record<string, any>, sessionToken: string, codeVerifier: string, loginUsername?: string): Promise<void> {
    this.setAuthenticationOrigin(window.location.pathname)

    // Ensure we have a valid username - prioritize the login username over user object
    const username = loginUsername || user.username || user.preferred_username || user.email || 'unknown'

    const authenticatedSession: IMkAuthenticatedSession = {
      sessionType: 'authenticated',
      authBrand: this.authBrand,
      authenticatedUser: user,
      codeVerifier,
      sessionToken,
      username,
      recoveryToken: user.recoveryToken || '',
    }

    ProtectedDataStore.setItem<IMkAuthenticatedSession>(EMkStorageKeys.MAKO_SESSION, { ...authenticatedSession })
    this.dispatch({ type: EMkAuthenticationAction.AUTHENTICATION_SUCCESS, payload: { ...authenticatedSession } })
  }

  private async authorizeSession(initializedSession: IMkAuthenticatedSession, makoUser: IMkUser, oktaUserName: string, tokens: any): Promise<IMkAuthorizedSession> {
    const session: IMkAuthorizedSession = {
      ...initializedSession,
      authBrand: this.authBrand,
      authorizedUser: makoUser,
      authenticatedUserName: initializedSession.username || oktaUserName,
      accessCode: makoUser.accessCode,
      jwtToken: makoUser.jwtToken,
      memberAccessToken: makoUser.memberAccessToken,
      sessionType: 'authorized',
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
    }

    ProtectedDataStore.setItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION, { ...session })
    this.persistSelectedMemberNumber(makoUser.memberNumber)
    // Set TTL for the session and schedule a proactive refresh of memberAccessToken
    MkAuthenticationService.setMakoAuthTokensExpiry()

    if (session.authenticatedUserName && session.accessCode) {
      const apexDomain = getApexDomain(window.location.hostname)

      ProtectedDataStore.setCookie('user_name', session.authenticatedUserName, {
        expires: new Date(Date.now() + AUTH_COOKIE_EXPIRATION_HOURS * 60 * 60 * 1000),
        path: '/',
        domain: apexDomain,
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        shouldEncrypt: false,
      })

      ProtectedDataStore.setCookie('access_code', session.accessCode, {
        expires: new Date(Date.now() + AUTH_COOKIE_EXPIRATION_HOURS * 60 * 60 * 1000),
        path: '/',
        domain: apexDomain,
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        shouldEncrypt: false,
      })
    }

    this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_SUCCESS, payload: { ...session } })
    return session
  }

  // Refresh auth-related tokens (jwtToken, memberAccessToken, accessCode) and update the stored session
  public async refreshMakoAuthorizationTokens(): Promise<void> {
    // Prevent overlapping refresh executions
    if (this.isRefreshingMakoAuthTokens) {
      return
    }
    this.isRefreshingMakoAuthTokens = true
    try {
      // Execute only when auth tokens are NOT expired
      const expired = this.isMakoAuthTokensExpired()
      if (!expired) {
        return
      }
      // Read the current session
      const stored = await ProtectedDataStore.getItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION)
      if (!stored || stored.sessionType !== 'authorized') {
        throw new AuthorizationError('No authorized session to refresh')
      }

      // Call backend to get a fresh token. Reuse the same authorization endpoint flow.
      const accessToken = (stored as any).accessToken?.accessToken
      const userName = stored.authenticatedUserName
      if (!accessToken || !userName) {
        throw new AuthorizationError('Missing token or username for refresh')
      }

      // Reuse existing logic to authorize with Mako and get a fresh user payload
      const { makoUser } = await this.authorizeWithMako({ accessToken, userName })

      // Update tokens from the refreshed user payload
      const updated: IMkAuthorizedSession = {
        ...stored,
        authorizedUser: makoUser,
        accessCode: makoUser.accessCode,
        jwtToken: makoUser.jwtToken,
        memberAccessToken: makoUser.memberAccessToken,
      }

      // Persist and reset expiration
      ProtectedDataStore.setItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION, updated)
      this.persistSelectedMemberNumber(updated.authorizedUser?.memberNumber)
      MkAuthenticationService.setMakoAuthTokensExpiry()

      // Notify consumers (reducers/UI) about refreshed authorization data
      this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_SUCCESS, payload: { ...updated } })
    } catch (err) {
      const authErr = err instanceof AuthorizationError ? err : new AuthorizationError('Failed to refresh authorization tokens', err as Error)
      this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_FAILURE, payload: authErr })
    } finally {
      this.isRefreshingMakoAuthTokens = false
    }
  }

  public async switchMembership(memberNumber: string): Promise<void> {
    const normalizedMemberNumber = memberNumber.trim()

    if (!normalizedMemberNumber) {
      throw new AuthorizationError('Member number is required to switch memberships')
    }

    try {
      const stored = await ProtectedDataStore.getItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION)
      if (!stored || stored.sessionType !== 'authorized') {
        throw new AuthorizationError('No authorized session available to switch memberships')
      }

      if (stored.authorizedUser?.memberNumber === normalizedMemberNumber) {
        this.persistSelectedMemberNumber(normalizedMemberNumber)
        return
      }

      const accessToken = stored.accessToken?.accessToken
      const userName = stored.authenticatedUserName
      if (!accessToken || !userName) {
        throw new AuthorizationError('Missing token or username for membership switch')
      }

      const { makoUser } = await this.authorizeWithMako({
        accessToken,
        userName,
        memberNumber: normalizedMemberNumber,
      })

      if (makoUser.memberNumber !== normalizedMemberNumber) {
        throw new AuthorizationError(`Requested membership ${normalizedMemberNumber} was not returned by authorization`)
      }

      const updated: IMkAuthorizedSession = {
        ...stored,
        authorizedUser: makoUser,
        accessCode: makoUser.accessCode,
        jwtToken: makoUser.jwtToken,
        memberAccessToken: makoUser.memberAccessToken,
      }

      ProtectedDataStore.setItem<IMkAuthorizedSession>(EMkStorageKeys.MAKO_SESSION, updated)
      this.persistSelectedMemberNumber(normalizedMemberNumber)
      MkAuthenticationService.setMakoAuthTokensExpiry()
      this.dispatch({ type: EMkAuthenticationAction.AUTHORIZATION_SUCCESS, payload: { ...updated } })
    } catch (err) {
      const authErr = err instanceof AuthorizationError ? err : new AuthorizationError('Failed to switch memberships', err as Error)
      this.emitAuthorizationError(authErr)
      throw authErr
    }
  }

  private async destroySession(): Promise<void> {
    // Preserve valid redirect paths (non-login paths) during session clearing
    // This allows redirectTo parameters to persist through the login flow
    const existingOrigin = DataStore.getItem<string>(EMkStorageKeys.LOGIN_ORIGIN)
    const shouldPreserveOrigin = existingOrigin && !LOGIN_PATHS.includes(existingOrigin as (typeof LOGIN_PATHS)[number])

    // Only clear LOGIN_ORIGIN if it's not a valid redirect target
    if (!shouldPreserveOrigin) {
      this.removeAuthenticationOrigin()
      DataStore.removeItem(EMkStorageKeys.LOGIN_ORIGIN)
    }

    // Clear all Okta-related storage from localStorage
    DataStore.bulkRemoveItems([OKTA_CACHE_STORAGE, OKTA_SHARED_TRANSACTION_STORAGE, OKTA_ORIGINAL_URI_STORAGE, OKTA_TOKEN_STORAGE, OKTA_TOKEN_STORAGE_KEYS], {
      store: localStorage,
    })

    // Clear session storage items that might persist user data
    DataStore.bulkRemoveItems(['mfaSetupData', 'okta-auth-js-redirect-uri'], { store: sessionStorage })

    // Clear account context cache from session storage
    DataStore.bulkRemoveItems([...ACCOUNT_CONTEXT_CACHE_KEYS], { store: sessionStorage })

    // Drop cached resort list so the next load refetches the anonymous (full) catalog after logout
    DataStore.removeItem(EMkStorageKeys.MAKO_RESORTS)

    ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_SESSION)

    // Clear registration context to reset modal state after logout
    ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, { store: localStorage })

    // Also clear session expiry
    ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_AUTH_TOKENS_EXPIRES_AT)
    this.persistSelectedMemberNumber()

    // Clear authentication cookies
    const apexDomain = getApexDomain(window.location.hostname)
    ProtectedDataStore.removeCookie('user_name', { path: '/', domain: apexDomain })
    ProtectedDataStore.removeCookie('access_code', { path: '/', domain: apexDomain })

    this.authBrand = (this.makoState as any).authBrand as string
    this.dispatch({ type: EMkAuthenticationAction.REVOKE_AUTHENTICATION })
    this.dispatch({ type: EMkAuthenticationAction.CLEAR_ERROR })
    await this.oktaAuth?.tokenManager.clear()
  }

  private getSelectedMemberNumberFromStorage(): string | undefined {
    try {
      const storageKey = (this.constructor as typeof MkAuthenticationService).SELECTED_MEMBER_NUMBER_STORAGE_KEY
      const storedMemberNumber = sessionStorage.getItem(storageKey)
      const normalizedMemberNumber = storedMemberNumber?.trim()
      return normalizedMemberNumber || undefined
    } catch {
      return undefined
    }
  }

  private persistSelectedMemberNumber(memberNumber?: string): void {
    try {
      const storageKey = (this.constructor as typeof MkAuthenticationService).SELECTED_MEMBER_NUMBER_STORAGE_KEY
      if (memberNumber && memberNumber.trim()) {
        sessionStorage.setItem(storageKey, memberNumber.trim())
        return
      }

      sessionStorage.removeItem(storageKey)
    } catch {
      // Ignore storage errors and keep auth flow intact
    }
  }

  private async authenticateWithOkta(username: string, password: string) {
    try {
      if (!this.oktaConfig) throw new AuthenticationError('Okta config is not initialized')
      if (!this.oktaAuth) throw new AuthenticationError('Okta auth is not initialized')

      if (this.makoState.isAccountLocked && this.makoState.lockedUsername === username) {
        throw new AuthenticationError('OKTA_ACCOUNT_LOCKED')
      }

      const authnResponse = (await this.oktaAuth.signInWithCredentials({ username, password })) as unknown as OktaAuthnResponse

      if (authnResponse.status === 'LOCKED_OUT') {
        const lockoutEndTime = Date.now() + ACCOUNT_LOCKOUT_DURATION_MINUTES * 60 * 1000
        this.dispatch({
          type: EMkAuthenticationAction.LOCK_ACCOUNT,
          payload: { lockoutEndTime, username },
        })
        throw new AuthenticationError('OKTA_ACCOUNT_LOCKED')
      }

      if (authnResponse.status === 'PASSWORD_EXPIRED') {
        throw new AuthenticationError('Your password has expired. Please reset your password to continue.')
      }

      if (!authnResponse.user) throw new AuthenticationError('Session user is undefined')

      if (authnResponse.status === 'MFA_ENROLL') {
        const mfaFactors = [...(authnResponse._embedded?.factors || authnResponse.data?._embedded?.factors || [])]

        // For MFA_ENROLL status, the stateToken might be in different locations
        // Check multiple possible locations for the stateToken
        let stateToken = authnResponse.stateToken || ''

        // Check if stateToken is in the data object
        if (!stateToken && authnResponse.data?.stateToken) {
          stateToken = authnResponse.data.stateToken
        }

        // Check if stateToken is in the _embedded object
        if (!stateToken && authnResponse._embedded?.stateToken) {
          stateToken = authnResponse._embedded.stateToken
        }

        // Check if stateToken is in the response object itself
        if (!stateToken && authnResponse.response?.stateToken) {
          stateToken = authnResponse.response.stateToken
        }

        // Extract email from multiple possible locations
        // For login flow, email may be in:
        // 1. _embedded.user.profile.email (if backend injects it, like reset_password)
        // 2. _embedded.factors[]._embedded.emails[].email (email factor's available emails) - PRIMARY for login
        // 3. user.profile.email (direct user profile)
        // 4. Fallback to username
        const emailFromEmbeddedUser = authnResponse._embedded?.user?.profile?.email || authnResponse.data?._embedded?.user?.profile?.email
        const emailFromUser = authnResponse.user?.profile?.email
        // Check email factor's embedded emails array (this is where login flow stores email)
        let emailFromFactors = ''
        const emailFactor =
          authnResponse._embedded?.factors?.find((f: any) => f.factorType === 'email') || authnResponse.data?._embedded?.factors?.find((f: any) => f.factorType === 'email')
        if (emailFactor?._embedded?.emails && Array.isArray(emailFactor._embedded.emails) && emailFactor._embedded.emails.length > 0) {
          emailFromFactors = emailFactor._embedded.emails[0]?.email || emailFactor._embedded.emails[0]?.profile?.email || ''
        }
        const email = emailFromEmbeddedUser || emailFromFactors || emailFromUser || username || ''
        const firstName = getAuthnProfileName(authnResponse, 'firstName')
        const lastName = getAuthnProfileName(authnResponse, 'lastName')

        // Check rememberMe and save username to localStorage
        const savedRememberMe = DataStore.getItem('rememberMe', { store: localStorage })
        if (savedRememberMe === 'true' && username) {
          DataStore.setItem('savedUsername', username, { store: localStorage })
        }

        if (stateToken) {
          saveRegistrationContext({
            stateToken,
            mfaFactors,
            username,
            email,
            firstName,
            lastName,
          })
        } else {
          saveRegistrationContext({
            stateToken: '',
            mfaFactors,
            username,
            email,
            firstName,
            lastName,
          })
        }

        throw new AuthenticationError('MFA_ENROLL_REQUIRED')
      }

      if (authnResponse.status === 'MFA_REQUIRED') {
        const enrolledFactors = [...(authnResponse._embedded?.factors || authnResponse.data?._embedded?.factors || [])]
        let stateToken = authnResponse.stateToken || ''

        if (!stateToken && authnResponse.data?.stateToken) {
          stateToken = authnResponse.data.stateToken
        }

        if (!stateToken && authnResponse._embedded?.stateToken) {
          stateToken = authnResponse._embedded.stateToken
        }

        if (!stateToken && authnResponse.response?.stateToken) {
          stateToken = authnResponse.response.stateToken
        }

        const firstName = getAuthnProfileName(authnResponse, 'firstName')
        const lastName = getAuthnProfileName(authnResponse, 'lastName')
        const savedRememberMe = DataStore.getItem('rememberMe', { store: localStorage })
        if (savedRememberMe === 'true' && username) {
          DataStore.setItem('savedUsername', username, { store: localStorage })
        }

        const primaryFactor = enrolledFactors[0]
        if (!primaryFactor) {
          throw new AuthenticationError('MFA verification failed: No enrolled factors')
        }

        const email = resolveAuthnMfaDisplayEmail(authnResponse, username, primaryFactor)
        const mfaEmail = primaryFactor.factorType === 'email' ? primaryFactor.profile?.email : undefined

        const verifyLink = primaryFactor._links?.verify?.href
        const phoneNumber = primaryFactor.profile?.phoneNumber || ''

        if (stateToken) {
          const contextSaved = saveRegistrationContext({
            stateToken,
            mfaFactors: enrolledFactors,
            username,
            email,
            mfaEmail,
            firstName,
            lastName,
            factorProfile: primaryFactor.profile,
            phone: phoneNumber,
          })

          if (contextSaved) {
            DataStore.setItem('mfaFactorId', primaryFactor.id)

            if (verifyLink) {
              DataStore.setItem('mfaNextLink', verifyLink)
            }

            if (verifyLink && !MkAuthenticationService.hasRecentlyTriggeredMfa(stateToken)) {
              MkAuthenticationService.markMfaTriggered(stateToken)
              try {
                const response = await fetch(`${serverUrl}${API}/okta/authn/factors/${primaryFactor.id}/verify`, {
                  method: 'POST',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    stateToken,
                  }),
                })

                if (response.ok) {
                  const responseData = await response.json()

                  // Extract resend link from response for later use
                  const resendLink = Array.isArray(responseData._links?.resend) ? responseData._links.resend[0]?.href : responseData._links?.resend?.href

                  if (resendLink) {
                    // Update registration context with resend link
                    try {
                      const existingContext = ProtectedDataStore.getItem<RegistrationContext>(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
                        store: localStorage,
                      })

                      if (existingContext) {
                        const updatedContext = {
                          ...existingContext,
                          resendLink,
                        }

                        ProtectedDataStore.setItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, updatedContext, {
                          store: localStorage,
                          expirationTime: existingContext.expiresAt,
                        })
                      }
                    } catch (updateError) {
                      // Failed to update registration context with resend link
                    }
                  }
                } else {
                  await response.json().catch(() => ({}))
                }
              } catch (triggerError) {
                // Error triggering verification code send
              }
            }
          }
        }

        // Throw error to trigger modal opening at MFA_VERIFY_CODE step
        throw new AuthenticationError('MFA_VERIFICATION_REQUIRED')
      }

      // Handle normal SUCCESS case
      if (!authnResponse.sessionToken) throw new AuthenticationError('Session token is undefined')

      const { codeVerifier } = await this.oktaAuth.token.prepareTokenParams({
        ...this.oktaConfig,
        sessionToken: authnResponse.sessionToken,
      })

      this.setAuthenticationOrigin(window.location.pathname)

      try {
        await this.oktaAuth.token.getWithRedirect({
          ...this.oktaConfig,
          sessionToken: String(authnResponse.sessionToken),
          codeVerifier: String(codeVerifier),
        })
      } catch (networkError) {
        throw new AuthenticationError('Failed to redirect to Okta', networkError)
      }

      return {
        user: authnResponse.user,
        sessionToken: String(authnResponse.sessionToken),
        codeVerifier: String(codeVerifier),
      }
    } catch (error) {
      // Don't wrap specific authentication errors that should be shown to users
      if (error instanceof AuthenticationError) {
        const preservedErrors: string[] = [
          AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED,
          AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED,
          AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED,
          AUTH_ERROR_TYPES.PASSWORD_EXPIRED,
        ]

        if (preservedErrors.includes(error.message)) {
          throw error
        }
      }

      // Check if the error is an Okta API error with response data
      const oktaError = error as any

      if (oktaError?.xhr || oktaError?.responseJSON || oktaError?.errorSummary) {
        // Try to extract the actual Okta response from the error
        let oktaResponse: any = null

        // Check multiple possible locations for the response data
        if (oktaError.xhr?.responseText) {
          try {
            oktaResponse = JSON.parse(oktaError.xhr.responseText)
          } catch (parseError) {
            // Failed to parse xhr.responseText
          }
        } else if (oktaError.xhr?.response) {
          oktaResponse = oktaError.xhr.response
        } else if (oktaError.responseJSON) {
          oktaResponse = oktaError.responseJSON
        } else if (oktaError.response) {
          oktaResponse = oktaError.response
        }

        // Check for LOCKED_OUT status
        if (oktaResponse?.status === 'LOCKED_OUT') {
          // Set lockout time (Okta manages the actual unlock, but we show a 15 min countdown)
          const lockoutEndTime = Date.now() + ACCOUNT_LOCKOUT_DURATION_MINUTES * 60 * 1000

          // Dispatch MFA lock to drive UI to the critical lock screen
          this.dispatch({
            type: EMkAuthenticationAction.LOCK_MFA_ACCOUNT,
            payload: { lockoutEndTime, username },
          })

          // Throw a normalized error for downstream routing
          throw new AuthenticationError('OKTA_ACCOUNT_LOCKED')
        }

        // Check for PASSWORD_EXPIRED status
        if (oktaResponse?.status === 'PASSWORD_EXPIRED') {
          throw new AuthenticationError('Your password has expired. Please reset your password to continue.')
        }

        // Check for error summary that might contain locked account information
        if (oktaError.errorSummary && oktaError.errorSummary.toLowerCase().includes('locked')) {
          // Set lockout time (Okta manages the actual unlock, but we show a 15 min countdown)
          const lockoutEndTime = Date.now() + ACCOUNT_LOCKOUT_DURATION_MINUTES * 60 * 1000

          // Dispatch MFA lock to drive UI to the critical lock screen
          this.dispatch({
            type: EMkAuthenticationAction.LOCK_MFA_ACCOUNT,
            payload: { lockoutEndTime, username },
          })

          // Throw a normalized error for downstream routing
          throw new AuthenticationError('OKTA_ACCOUNT_LOCKED')
        }
      }

      throw new AuthenticationError('Failed to authenticate with Okta', error)
    }
  }

  private async authorizeWithMako({ accessToken, userName, memberNumber }: { accessToken: string; userName: string; memberNumber?: string }): Promise<{
    authenticatedSession: IMkAuthenticatedSession
    makoUser: IMkUser
  }> {
    // Validate session consistency before proceeding
    const authenticatedSession = await this.validateSessionConsistency(userName)

    try {
      const authorizationResponse = await this.makeAuthorizationRequest(accessToken, userName, memberNumber)
      const responseData = await authorizationResponse.json()

      // Check for error in response body even if status is 200 OK
      if (responseData.errorCode && responseData.errorCode !== 'OK') {
        const errorMessage = this.formatAccountErrorMessage(responseData.errorCode, responseData.errorMessage)
        throw new AuthorizationError(errorMessage)
      }

      // Use the current Okta user data as the base, not potentially stale session data
      const userData = authenticatedSession.authenticatedUser as IMkAuthenticatedSession['authenticatedUser']
      const makoUser = {
        ...userData,
        ...responseData,
        useByDate: responseData.useByDate || 'Jun 30, 2026',
        // Ensure we're using the current username from Okta tokens
        username: userName,
      } as IMkUser

      MkAuthenticationService.validateMakoUser(makoUser)

      return {
        authenticatedSession,
        makoUser,
      }
    } catch (error) {
      if (error instanceof AuthorizationError) {
        throw error
      }
      throw new AuthorizationError('Failed to authorize with Mako', error)
    }
  }

  private async makeAuthorizationRequest(accessToken: string, userName: string, memberNumber?: string): Promise<Response> {
    const requestBody = {
      accessToken,
      userName,
      ...(memberNumber ? { memberNumber } : {}),
    }

    try {
      const authorizationResponse = await fetch(`${serverUrl}${API}/authorize`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          Brand: Brand.getKey(this.authBrand),
        },
        body: JSON.stringify(requestBody),
      })

      if (!authorizationResponse.ok) {
        await this.handleAuthorizationError(authorizationResponse)
      }

      return authorizationResponse
    } catch (networkError) {
      // Try to extract a user-friendly error message
      let errorMessage = 'Unknown network error'

      if (networkError instanceof Error) {
        errorMessage = networkError.message

        // If the error message contains JSON, try to parse it
        if (errorMessage.includes('{') && errorMessage.includes('errorCode')) {
          try {
            // Try to extract JSON from the error message (handle multiline with [\s\S])
            const jsonMatch = errorMessage.match(/\{[\s\S]*\}/)
            if (jsonMatch) {
              const errorData = JSON.parse(jsonMatch[0])
              if (errorData.errorCode && errorData.errorMessage) {
                errorMessage = this.formatAccountErrorMessage(errorData.errorCode, errorData.errorMessage)
              }
            }
          } catch (parseError) {
            // If parsing fails, use the original message but clean it up
            this.logger.warn('Failed to parse error JSON from network error', { errorMessage, parseError })
          }
        }
      }

      // Only prefix with "Network error" if it's actually a network issue
      if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('Network')) {
        throw new AuthorizationError(`Network error during authorization: We are experiencing technical difficulties. Please try again later.`)
      }

      throw new AuthorizationError(errorMessage)
    }
  }

  private async handleAuthorizationError(authorizationResponse: Response): Promise<never> {
    const errorText = await this.extractErrorText(authorizationResponse)
    const userFriendlyMessage = parseApiErrorResponse(errorText, authorizationResponse.status, this.logger)

    throw new AuthorizationError(userFriendlyMessage)
  }

  /**
   * Formats account error messages into user-friendly text
   * Delegates to shared utility to avoid code duplication
   */
  // eslint-disable-next-line class-methods-use-this
  private formatAccountErrorMessage(errorCode: string, errorMessage?: string | null): string {
    return formatAccountError(errorCode, errorMessage)
  }

  // eslint-disable-next-line class-methods-use-this
  private async extractErrorText(authorizationResponse: Response): Promise<string> {
    const responseClone = authorizationResponse.clone()
    let errorText = ''

    try {
      // Try to read the original response first
      errorText = await authorizationResponse.text()

      // If original is empty, try the clone
      if (!errorText || errorText.trim() === '') {
        const clonedText = await responseClone.text()
        if (clonedText && clonedText.trim() !== '') {
          errorText = clonedText
        }
      }
    } catch (textError) {
      // Try to read from clone as fallback
      try {
        const clonedText = await responseClone.text()
        if (clonedText && clonedText.trim() !== '') {
          errorText = clonedText
        }
      } catch (cloneError) {
        // Failed to read cloned response text
      }
    }

    return errorText
  }

  private static validateMakoUser(makoUser: IMkUser): void {
    if (!makoUser.accessCode) throw new AuthorizationError('Access code is undefined')
    if (!makoUser.jwtToken) throw new AuthorizationError('JWT token is undefined')
    if (!makoUser.memberAccessToken) throw new AuthorizationError('Member access token is undefined')
    if (!['OK'].includes(makoUser.errorCode as string)) {
      // This should rarely be reached now since we check errorCode in authorizeWithMako
      // But keep it as a safety check with formatted message
      const errorMessage = makoUser.errorMessage || 'Account error occurred'
      const formattedMessage = this.formatAccountErrorMessageStatic(makoUser.errorCode as string, errorMessage)
      throw new AuthorizationError(formattedMessage)
    }
  }

  /**
   * Static version of formatAccountErrorMessage for use in static methods
   * Delegates to shared utility to avoid code duplication
   */
  private static formatAccountErrorMessageStatic(errorCode: string, errorMessage?: string | null): string {
    return formatAccountError(errorCode, errorMessage)
  }

  private async authenticateWithToken(token: string, isActivation: boolean = false): Promise<OktaAuthnResponse> {
    const orgDomain = MkAuthenticationService.extractOktaOrgDomain(this.oktaConfig.issuer)
    // Okta uses the recovery token endpoint for both activation and password reset flows
    const authnUrl = `${orgDomain}/api/v1/authn/recovery/token`
    const payload = { recoveryToken: token }

    try {
      const response = await fetch(authnUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          // Do not send SSWS admin token; this endpoint is unauthenticated by design
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        await this.handleTokenAuthenticationError(response, isActivation)
      }

      const authnResponse = (await response.json()) as OktaAuthnResponse
      this.validateTokenResponse(authnResponse)

      return authnResponse
    } catch (err: any) {
      if (err instanceof AuthorizationError) {
        throw err
      }
      throw new AuthorizationError('Failed to authenticate with Okta', err)
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private async handleTokenAuthenticationError(response: Response, isActivation: boolean): Promise<never> {
    const errorText = await response.text()

    // Handle specific error cases for better user experience
    if (isActivation && response.status === 401) {
      const errorData = MkAuthenticationService.parseErrorResponse(errorText)
      if (errorData?.errorCode === 'E0000004' && errorData.errorSummary?.includes('Authentication failed')) {
        // For activation tokens that have already been consumed, we can treat this as a non-critical error
        // since the first call likely succeeded
        throw new AuthorizationError('Token has already been processed. Please check if your account is already activated.')
      }
    }

    throw new Error(`HTTP ${response.status}: ${response.statusText}: ${errorText}`)
  }

  private static parseErrorResponse(errorText: string): any {
    try {
      return JSON.parse(errorText)
    } catch {
      return null
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private validateTokenResponse(authnResponse: OktaAuthnResponse): void {
    // For activation tokens, the expected status is 'PASSWORD_RESET', not 'SUCCESS'
    if ((authnResponse.status !== 'SUCCESS' && authnResponse.status !== 'PASSWORD_RESET') || !authnResponse.stateToken) {
      throw new Error(authnResponse.errorSummary || 'Failed to authenticate with Okta')
    }
  }

  private async clearStaleSessions(): Promise<void> {
    // Clear any existing session from storage
    await this.destroySession()

    // Clear Okta tokens to prevent retrieving stale tokens from previous user
    await this.clearOktaTokens()

    // Additional cleanup for browser storage that might persist
    await this.clearBrowserStorage()
  }

  private async clearOktaTokens(): Promise<void> {
    try {
      if (this.oktaAuth) {
        await this.oktaAuth.tokenManager.clear()
      }
    } catch (tokenError) {
      // Failed to clear Okta tokens during cleanup
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private async clearBrowserStorage(): Promise<void> {
    try {
      // Clear any remaining Okta-related storage
      DataStore.bulkRemoveItems([OKTA_CACHE_STORAGE, OKTA_SHARED_TRANSACTION_STORAGE, OKTA_ORIGINAL_URI_STORAGE, OKTA_TOKEN_STORAGE, OKTA_TOKEN_STORAGE_KEYS], {
        store: localStorage,
      })

      // Clear session storage items
      DataStore.bulkRemoveItems(['mfaSetupData', 'okta-auth-js-redirect-uri'], { store: sessionStorage })

      DataStore.bulkRemoveItems([...ACCOUNT_CONTEXT_CACHE_KEYS], { store: sessionStorage })
    } catch {
      // Ignore browser storage cleanup errors.
    }
  }

  private async validateSessionConsistency(userName: string): Promise<IMkAuthenticatedSession> {
    const storedSession = await ProtectedDataStore.getItem<TMkSession>(EMkStorageKeys.MAKO_SESSION)

    // No stored session is OK for fresh logins - return minimal session
    if (!storedSession) {
      return {
        sessionType: 'authenticated',
        authBrand: this.authBrand,
        authenticatedUser: {},
        codeVerifier: '',
        username: userName,
        sessionToken: '',
        recoveryToken: '',
      }
    }

    const storedUsername =
      storedSession.sessionType === 'authorized' ? (storedSession as IMkAuthorizedSession).authenticatedUserName : (storedSession as IMkAuthenticatedSession).username

    const hasValidStoredUsername = storedUsername && storedUsername !== '' && storedUsername !== 'unknown'

    if (!hasValidStoredUsername) {
      await this.destroySession()
      throw new AuthorizationError('Invalid session - please log in again')
    }

    if (storedUsername.toLowerCase() !== userName.toLowerCase()) {
      await this.destroySession()
      throw new AuthorizationError('Session mismatch - please log in again')
    }

    if (storedSession.sessionType === 'authorized') {
      const authorizedSession = storedSession as IMkAuthorizedSession
      return {
        sessionType: 'authenticated',
        authBrand: authorizedSession.authBrand,
        authenticatedUser: authorizedSession.authorizedUser,
        codeVerifier: '',
        username: authorizedSession.authenticatedUserName,
        sessionToken: authorizedSession.sessionToken,
        recoveryToken: authorizedSession.recoveryToken,
      }
    }

    return storedSession as IMkAuthenticatedSession
  }

  private static extractOktaOrgDomain(issuer: string): string {
    if (!issuer) {
      throw new Error('Issuer URL is required')
    }

    const orgDomain = issuer.replace(/\/oauth2\/[^/]+$/, '')

    if (!orgDomain || orgDomain === issuer) {
      throw new Error(`Invalid Okta issuer URL format: ${issuer}. Expected format: https://your-org.okta.com/oauth2/authorizationServerId`)
    }

    return orgDomain
  }
}
